<template>
  <b-row class="margin-0">
    <b-col cols="12" class="padding-0">
      <div
        v-if="mode === 'EDIT'"
        class="font-size-16 font-weight-500 color-complementary-400 margin-bottom-24"
      >
        <span
          class="color-complementary-300 icon-hover"
          @click.stop="handleCloseDropdownModal"
        >
          <mdicon name="close" size="24" />
        </span>
        {{ $lang.error }}
      </div>
      <div
        v-if="evidence"
        class="font-size-12 color-complementary-400 margin-bottom-24"
      >
        {{ $lang.evidence }}
        <div class="font-size-14 color-complementary">
          {{ evidence }}
        </div>
      </div>
      <div
        v-if="behavioralWitness"
        class="font-size-12 color-complementary-400 margin-bottom-24"
      >
        {{ $lang.behavioralWitness }}
        <div class="font-size-14 color-complementary">
          {{ behavioralWitness }}
        </div>
      </div>
    </b-col>
    <b-col cols="12" class="padding-0">
      <EnumDropdown
        :title="$lang.errorLabel"
        :label="$lang.errorLabel"
        :placeholder="$lang.errorLabel"
        :value="form.errorLabel"
        name="errorLabel"
        id="errorLabel"
        categoryName="error_code"
        :vuelidateObj="$v.form.errorLabel"
        :parentOptions="errorCodeOptions"
        :disabled="!hasItemPermission(['ERROR_CAPTION_EDIT'])"
        @input="input"
        @focus="resetValidation"
        @blur="checkValidation"
      />
    </b-col>
    <b-col cols="12" class="padding-0">
      <Input
        :title="$lang.errorDescription"
        :label="$lang.errorDescription"
        :placeholder="$lang.errorDescription"
        :value="form.errorDescription"
        name="errorDescription"
        id="errorDescription"
        type="textarea"
        :minLength="lengthNumber3"
        :maxLength="lengthNumber256"
        :vuelidateObj="$v.form.errorDescription"
        :disabled="!hasItemPermission(['ERROR_DESCRIPTION_EDIT'])"
        @input="input"
        @focus="resetValidation"
        @blur="checkValidation"
      />
    </b-col>
    <b-col cols="12" class="flex-end padding-0">
      <Button
        v-if="hasCancelButton"
        class="margin-left-8"
        :wrapperClass="
          checkScreenDevice(['mobile', 'tablet']) ? 'width-percent-100' : ''
        "
        contentClass="justify-content-center"
        variant="filled-complementary"
        :text="$lang.cancel"
        @click="handleClose()"
      />
      <Button
        v-if="
          hasItemPermission([
            'ERROR_ADD',
            'ERROR_CAPTION_EDIT',
            'ERROR_DESCRIPTION_EDIT',
          ])
        "
        type="submit"
        :wrapperClass="
          checkScreenDevice(['mobile', 'tablet']) ? 'width-percent-100' : ''
        "
        contentClass="justify-content-center"
        variant="primary"
        :text="$lang.submit"
        :loading="loading"
        @click="handleSubmit"
      />
    </b-col>
  </b-row>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import { Input, EnumDropdown, Button } from "@/components/re-design/forms";
import { required } from "vuelidate/lib/validators";
import {
  generateError,
  renderEumOptions,
  checkScreenDevice,
} from "@/shared/utils";
import { setCenterExecuteCaseCompetencyIndicatorEvidence } from "@/modules/kanoon/services";
import { pathOr } from "ramda";
export default {
  mixins: [inputMixin],
  inject: [
    "CENTER_EXECUTE_ID",
    "CASE_ID",
    "COMPETENCY_ID",
    "INDICATOR_ID",
    "EVIDENCE_ID",
    "GET_INDICATORS",
    "hasItemPermission",
    "handleCloseDropdownModal",
  ],
  components: { Input, EnumDropdown, Button },
  props: {
    evidenceItem: {
      type: Object,
      default: null,
    },
    hasCancelButton: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "ADD",
    },
  },
  validations() {
    return {
      form: {
        errorLabel: { required },
        errorDescription: { required },
      },
    };
  },
  data() {
    return {
      form: {
        errorLabel: null,
        errorDescription: null,
      },
      loading: false,
    };
  },
  computed: {
    errorCodeOptions() {
      return renderEumOptions("error_code", true);
    },
    behavioralWitness() {
      return pathOr("", ["behavioralWitness"], this.evidenceItem);
    },
    evidence() {
      return pathOr("", ["caption"], this.evidenceItem);
    },
  },
  watch: {
    evidenceItem: {
      immediate: true,
      handler(value) {
        if (this.mode === "EDIT") {
          // select final score
          const errorLabelId = pathOr(null, ["error", "code"], value);
          this.form.errorLabel = this.errorCodeOptions.find(
            (option) => option.id === errorLabelId
          );

          this.form.errorDescription = pathOr(
            null,
            ["error", "description"],
            value
          );
        }
      },
    },
  },
  methods: {
    pathOr,
    checkScreenDevice,
    handleClose() {
      this.$emit("handleClose");
    },
    async handleSubmit() {
      this.$v.$touch();
      if (this.$v.$anyError || this.loading) return;

      try {
        this.loading = true;
        const getIndicators = this.GET_INDICATORS;
        const {
          CENTER_EXECUTE_ID: centerExecuteId,
          CASE_ID: caseId,
          COMPETENCY_ID: competencyId,
          INDICATOR_ID: indicatorId,
          EVIDENCE_ID: evidenceId,
        } = this;

        const { errorLabel, errorDescription } = this.form;
        const errorCode = pathOr(null, ["id"], errorLabel);
        const behavioralWitness = pathOr(
          null,
          ["behavioralWitness"],
          this.evidenceItem
        );
        const isSelect = pathOr(null, ["isSelect"], this.evidenceItem);
        await setCenterExecuteCaseCompetencyIndicatorEvidence({
          centerExecuteId,
          caseId,
          competencyId,
          indicatorId,
          evidenceId,
          errorCode,
          errorDescription,
          behavioralWitness,
          isSelect,
        });
        this.handleClose();
        getIndicators();
      } catch (error) {
        generateError(error);
      } finally {
        this.loading = false;
      }
    },
    getErrorLabelOptions(options) {
      const errorCode = pathOr(null, ["error", "code"], this.evidenceItem);
      this.form.errorLabel = options.find(({ id }) => id === errorCode);
    },
  },
};
</script>
